import { forwardRef, useRef, useState, useEffect, useCallback, ChangeEvent } from 'react'
import mergeRefs from 'react-merge-refs'
import { createTextMaskInputElement } from 'text-mask-core'
import { Input, type CommonInputProps } from 'src/shared/ui/Input'

type Mask = Array<string | RegExp>

export type MaskedConfig = {
  currentCaretPosition: number
  rawValue: string
  previousConformedValue: string
  mask?: Mask | ((rawValue: string) => Mask)
  guide?: boolean
  showMask?: boolean
  placeholderChar?: string
  keepCharPositions?: boolean
  pipe?: (
    conformedValue: string,
    config: MaskedConfig,
  ) => false | string | { value: string; indexesOfPipedChars: number[] }
}
export type CommonMaskedInputProps = CommonInputProps & {
  mask?: MaskedConfig['mask']
  keepCharPositions?: MaskedConfig['keepCharPositions']
  onBeforeDisplay?: MaskedConfig['pipe']
}

/* TODO: Добавить обработку clear */

export const MaskedInput = forwardRef<HTMLInputElement, CommonMaskedInputProps>(
  (
    { mask, keepCharPositions, className, value, defaultValue, onChange, onBeforeDisplay, onClear, ...restProps },
    ref,
  ) => {
    const [inputNode, setInputNode] = useState<HTMLInputElement | null>(null)
    const textMask = useRef<ReturnType<typeof createTextMaskInputElement> | null>(null)

    const [inputValue, setInputValue] = useState(value || defaultValue || '')

    const update = useCallback(
      (newValue: string = '') => {
        if (textMask.current && inputNode) {
          textMask.current.update(newValue)

          setInputValue(inputNode.value)
        }
      },
      [inputNode],
    )

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        update(event.target.value)

        if (onChange) {
          onChange(event, { value: event.target.value })
        }
      },
      [onChange, update],
    )

    useEffect(() => {
      if (inputNode) {
        textMask.current = createTextMaskInputElement({
          mask,
          inputElement: inputNode,
          pipe: onBeforeDisplay,
          guide: false,
          keepCharPositions,
          showMask: false,
          rawValue: '',
          currentCaretPosition: 0,
          previousConformedValue: '',
        })
      }
    }, [mask, keepCharPositions, inputNode, onBeforeDisplay])

    useEffect(() => {
      update(value || defaultValue)
    }, [value, defaultValue, update])

    return (
      <Input
        {...restProps}
        ref={mergeRefs([ref, setInputNode])}
        className={className}
        value={inputValue}
        onChange={handleChange}
      />
    )
  },
)

MaskedInput.displayName = 'MaskedInput'
